import { Helmet } from "react-helmet-async";
import useSWR from "swr";
import { AxiosError } from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useScrollFadeIn } from "../../utils/hooks";
import type { ProductsResponse } from "../../utils/api";
import { getSummary, getProducts } from "../../utils/api";
import { MailButton } from "../../components/Button";
import ScrollToTopButton from "../../components/ScrollToTop/ScrollToTopButton";
import Footer from "../../components/Footer";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import userGuide from "../../assets/pdf/user-guide.pdf";
import { normalizeImagePath } from "../../utils/helpers";
// import campaignDetails from "../../assets/pdf/campaign_details_fanheater.pdf";

import {useFadeInOut} from '../../components/FadeInOut';
// 共通CSSは常に読み込み
import "@scss/pr_fh.scss";

export default function FanHeaterPage() {
    const { data: productsDataHeater } = useSWR(
        "productsDataHeater",
        () => getProducts("heater"),
        {
            revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
            onError: (err: AxiosError) => {
                console.error("Error fetching products heater data:", err);
                // ここでエラー表示などを行いたい場合は追加
            },
        }
    );
    const { data: summaryDataHeater } = useSWR(
        "summaryDataHeater",
        () => getSummary("heater"),
        {
            revalidateOnFocus: false, // ページフォーカス時の再フェッチを無効化
            onError: (err: AxiosError) => {
                console.error("Error fetching summary heater data:", err);
                // ここでエラー表示などを行いたい場合は追加
            },
        }
    );
    const [activeProducts, setActiveProducts] = useState<ProductsResponse[]>(
        []
    );
    const [smallProducts, setSmallProducts] = useState<ProductsResponse[]>([]);
    const [mediumProducts, setMediumProducts] = useState<ProductsResponse[]>(
        []
    );
    const [largeProducts, setLargeProducts] = useState<ProductsResponse[]>([]);
    const [recommendedProducts, setRecommendedProducts] = useState<
        ProductsResponse[]
    >([]);
    const [isActive, setIsActive] = useState(false);
    const {display, toggleDisplay, boxStyle } = useFadeInOut(0.2);
    const [tabItems, setTabs] = useState<number>(0);
    const tabRefSmall = useRef<HTMLInputElement>(null);
    const tabRefMedium = useRef<HTMLInputElement>(null);
    const tabRefLarge = useRef<HTMLInputElement>(null);
    const [dataListActive, setDataListActive] = useState<boolean[]>(
        new Array(50).fill(false)
    );
    const [accActive, setAccActive] = useState([false, true, true]);
    const [faqActive, setFaqActive] = useState<boolean[]>(
        new Array(20).fill(false)
    );
    const faqList = [
        {
            q: "誰でも購入できますか？",
            a: (
                <>
                    <p>
                        西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内※で都市ガスをご利用であれば、上記4社とガス・電気契約が無いお客さまもご購入いただけます。
                    </p>
                    <ul className="attListMini">
                        <li>
                            福岡・熊本・長崎の3県15市15町。詳しくは
                            <a
                                href="https://hd.saibugas.co.jp/business/gas/?&amp;_gl=1*admsyw*_ga*Mzc4MTA2MjgzLjE3Mzk3NjUxMzQ.*_ga_HYEYQESJTW*czE3NTQ0Njk1OTQkbzE2JGcwJHQxNzU0NDY5NTk0JGo2MCRsMCRoMA..&amp;_ga=2.124442953.237105303.1754444476-378106283.1739765134#serviceArea"
                                target="_blank"
                                className="blueLink"
                            >
                                こちら
                            </a>
                            をご確認ください。
                        </li>
                    </ul>
                </>
            ),
        },
        {
            q: "購入できるファンヒーターの種類はこのサイト上の機種のみですか？",
            a: (
                <>
                    <p>
                        はい、本サイトに掲載している機種のみのお取扱いとなります。
                        <br />
                        その他の機種につきましては、最寄りの西部ガスリビングメイト店舗に直接お問い合わせください。
                    </p>
                </>
            ),
        },
        {
            q: "電話やFAXでも注文できますか？",
            a: (
                <>
                    <p>
                        西部ガスグループ公式オンラインショップでは、インターネットからのご注文のみ承っております。
                        <br />
                        お電話によるご注文は承っておりません。
                        <br />
                        ご了承ください。
                    </p>
                </>
            ),
        },
        {
            q: "注文履歴は確認できますか？",
            a: (
                <>
                    <p>
                        申し訳ございません。当サイトから確認することはできません。商品購入時にお送りしたSMSでご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "支払い方法は選べますか？",
            a: (
                <>
                    <p>
                        お支払い方法は
                        <a href="#payment" className="blueLink">
                            こちら
                        </a>
                        からご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "注文した商品の配送対応エリアを教えてください。",
            a: (
                <>
                    <p>
                        西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内※が配送エリアとなります。
                    </p>
                    <ul className="attListMini">
                        <li>
                            福岡・熊本・長崎の3県15市15町。詳しくは
                            <a
                                href="https://hd.saibugas.co.jp/business/gas/#serviceArea"
                                target="_blank"
                                className="blueLink"
                            >
                                こちら
                            </a>
                            をご確認ください。
                        </li>
                    </ul>
                </>
            ),
        },
        {
            q: "配送業者は指定できますか？",
            a: (
                <>
                    <p>
                        配送業者の指定はできません。弊社指定の販売店（もよりの西部ガス販売店（リビングメイト））より配送させて頂きます。
                    </p>
                </>
            ),
        },
        {
            q: "古い機器の回収はできますか？",
            a: (
                <>
                    <p>
                        ご注文をいただいた商品を取替後に不要となったガス機器は回収させていただきます。
                        <br />
                        ご注文いただいた商品以外の古い機器の回収は承っておりません。「各地方自治体の粗大ごみ扱い」にてご確認ください。
                    </p>
                </>
            ),
        },
        {
            q: "見積書や請求書の発行はできますか？",
            a: (
                <>
                    <p>
                        申し訳ありません。当ショップでは、見積書・請求書の発行は行っておりません。ご了承ください。
                    </p>
                </>
            ),
        },
        {
            q: "領収書の発行はできますか？",
            a: (
                <>
                    <p>
                        お客さまからご要望をいただいた場合、領収書を発行いたします。お手数かけしますが、領収書が必要な場合は
                        <a href="#contact" className="blueLink">
                            こちら
                        </a>
                        の問い合わせフォームより連絡お願いいたします。
                    </p>
                </>
            ),
        },
    ];

    const handleRecommendedClick = (productId: string) => {
        if (activeProducts.find((product) => product.id === productId)) {
            const product = activeProducts.find(
                (product) => product.id === productId
            );
            if (product?.heating_capacity === "small") {
                tabRefSmall.current?.click();
                window.location.href = `/pr_fh#${productId}`;
            } else if (product?.heating_capacity === "medium") {
                tabRefMedium.current?.click();
                window.location.href = `/pr_fh#${productId}`;
            } else if (product?.heating_capacity === "large") {
                tabRefLarge.current?.click();
                window.location.href = `/pr_fh#${productId}`;
            }
        }
        console.log("productId", productId);
    };
    const generateProducts = (products: ProductsResponse[]) => {
        return products.map((product, index) => {
            const discount =
                product.standard_price && product.standard_price > 0
                    ? true
                    : false;
            return (
                <li id={product.id} key={product.id} className="fade-in-up">
                    <h3
                        className={`main_ttl ${
                            product.is_outlet ? "outlet" : ""
                        }`}
                    >
                        <p style={{ fontSize: "1.2rem" }}>{product.comment}</p>
                        {product.name}
                    </h3>
                    <div className="product_list-inr">
                        <div className="productImg">
                            <img
                                src={normalizeImagePath(product.eyecatch_image_url)}
                                alt={`${product.name} ${product.model}`}
                            />
                        </div>
                        {discount && (
                            <p
                                className="standard_price"
                                style={{ marginBottom: "2rem" }}
                            >
                                標準価格&nbsp;
                                <span className="en">
                                    &yen;
                                    {product.standard_price?.toLocaleString()}
                                </span>
                                &nbsp;(税込)
                            </p>
                        )}

                        <div className="price">
                            {discount &&
                                product.discount_rate != null &&
                                product.discount_rate > 0 && (
                                    <div className="discount_rate">
                                        {product.discount_rate}%OFF
                                    </div>
                                )}
                            <div
                                style={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                }}
                            >
                                {product.is_limited &&
                                    product.inventory > 0 && (
                                        <span className="limited">
                                            先着{product.inventory}台限り
                                        </span>
                                    )}
                                <div style={{ lineHeight: "1" }}>
                                    {discount ? (
                                        <span>特別価格</span>
                                    ) : (
                                        <span>販売価格</span>
                                    )}
                                    <span className="en">
                                        &yen;
                                        {product.sales_price?.toLocaleString()}
                                    </span>
                                    (税込)
                                </div>
                            </div>
                        </div>
                        {product.inventory > 0 ? (
                            <Link
                                to={`/order/${product.id}`}
                                className="red_btn cart btn noto-sans-jp-black"
                                onClick={() => {
                                    const html = document.documentElement;
                                    html.style.scrollBehavior = "auto";
                                    setTimeout(() => {
                                        html.style.scrollBehavior = "smooth";
                                    }, 800);
                                }}
                            >
                                {product.is_low_stock && (
                                    <img
                                        className="low_stock_icon"
                                        src="/assets/img/common/low_stock.png"
                                        alt="残りわずか"
                                    />
                                )}
                                注文画面へ
                            </Link>
                        ) : (
                            <div className="red_btn cart btn noto-sans-jp-black">
                                注文画面へ
                                <div className="out_of_stock">完売しました</div>
                            </div>
                            // <Link
                            //     to={`/order/${product.id}`}
                            //     className="orange_btn cart btn"
                            //     onClick={() => {
                            //         const html = document.documentElement;
                            //         html.style.scrollBehavior = "auto";
                            //         setTimeout(() => {
                            //             html.style.scrollBehavior = "smooth";
                            //         }, 800);
                            //     }}
                            // >
                            //     在庫0テスト：注文画面へ
                            // </Link>
                        )}
                        <p className="annotation">
                            ※商品の引渡し後の返品または交換は、
                            <br />
                            原則として承りませんのでご注意ください（詳細は
                            <a
                                href={userGuide}
                                target="_blank"
                                className="blueLink"
                            >
                                こちら
                            </a>
                            ）
                        </p>
                        <motion.div
                            className={`dataList ${
                                dataListActive[index]
                                    ? "dataListOpen"
                                    : "dataListClose"
                            }`}
                            initial={false}
                            animate={{
                                height: dataListActive[index]
                                    ? "auto"
                                    : "100px",
                            }}
                            transition={{
                                duration: 0.2,
                                ease: [0.25, 0.46, 0.45, 0.94],
                            }}
                            onClick={() => {
                                setDataListActive((prev) => {
                                    const next = [...prev];
                                    next[index] = !next[index];
                                    return next;
                                });
                            }}
                        >
                            <dl>
                                <dt>メーカー</dt>
                                <dd>{product.manufacturer}</dd>
                            </dl>
                            <dl>
                                <dt>型式</dt>
                                <dd>{product.model}</dd>
                            </dl>
                            <dl>
                                <dt>サイズ(mm)</dt>
                                <dd>
                                    幅{product.size_width_mm}
                                    ×奥行
                                    {product.size_depth_mm}
                                    ×高さ
                                    {product.size_height_mm}
                                </dd>
                            </dl>
                            <dl>
                                <dt>重量(kg)</dt>
                                <dd>{product.weight_kg}</dd>
                            </dl>
                            <dl>
                                <dt>カラー</dt>
                                <dd>{product.color}</dd>
                            </dl>
                            <dl>
                                <dt>能力(kW)</dt>
                                <dd>{product.output_kw}</dd>
                            </dl>
                            <dl>
                                <dt>暖房のめやす</dt>
                                <dd>
                                    {product.heating_capacity === "small" &&
                                        "木造〜11畳・鉄筋〜15畳"}
                                    {product.heating_capacity === "medium" &&
                                        "木造〜12畳・鉄筋〜16畳"}
                                    {product.heating_capacity === "large" &&
                                        "木造〜15畳・鉄筋〜21畳"}
                                </dd>
                            </dl>
                        </motion.div>

                        <div className="swiper_wrap">
                            {product.carousel_images.length > 0 && (
                                <Swiper
                                    modules={[Pagination, Navigation]}
                                    loop={true}
                                    navigation={true}
                                    pagination={{
                                        clickable: true,
                                    }}
                                    slidesPerView={1}
                                    spaceBetween={10}
                                >
                                    {product.carousel_images
                                        .sort(
                                            (a, b) =>
                                                a.sort_order - b.sort_order
                                        )
                                        .map((image) => (
                                            <SwiperSlide key={image.id}>
                                                <img
                                                    src={normalizeImagePath(image.image_url)}
                                                    alt={`${product.name} ${product.model}`}
                                                />
                                            </SwiperSlide>
                                        ))}
                                </Swiper>
                            )}
                        </div>
                        <div className="btn">
                            {product.inventory > 0 ? (
                                <Link
                                    to={`/order/${product.id}`}
                                    className="red_btn cart btn noto-sans-jp-black"
                                    onClick={() => {
                                        const html = document.documentElement;
                                        html.style.scrollBehavior = "auto";
                                        setTimeout(() => {
                                            html.style.scrollBehavior =
                                                "smooth";
                                        }, 800);
                                    }}
                                >
                                    {product.is_low_stock && (
                                        <img
                                            className="low_stock_icon"
                                            src="/assets/img/common/low_stock.png"
                                            alt="残りわずか"
                                        />
                                    )}
                                    注文画面へ
                                </Link>
                            ) : (
                                <div className="red_btn cart btn noto-sans-jp-black">
                                    注文画面へ
                                    <div className="out_of_stock">
                                        完売しました
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </li>
            );
        });
    };
    useEffect(() => {
        if (productsDataHeater) {
            const actProducts = productsDataHeater.filter(
                (product) => product.is_active
            );
            setSmallProducts(
                actProducts
                    .filter((product) => product.heating_capacity === "small")
                    .sort(
                        (a, b) =>
                            (a?.display_sort_order ?? 0) -
                            (b?.display_sort_order ?? 0)
                    )
            );
            setMediumProducts(
                actProducts
                    .filter((product) => product.heating_capacity === "medium")
                    .sort(
                        (a, b) =>
                            (a?.display_sort_order ?? 0) -
                            (b?.display_sort_order ?? 0)
                    )
            );
            setLargeProducts(
                actProducts
                    .filter((product) => product.heating_capacity === "large")
                    .sort(
                        (a, b) =>
                            (a?.display_sort_order ?? 0) -
                            (b?.display_sort_order ?? 0)
                    )
            );
            setRecommendedProducts(
                actProducts
                    .filter((product) => product.is_recommended)
                    .sort(
                        (a, b) =>
                            (a?.recommended_sort_order ?? 0) -
                            (b?.recommended_sort_order ?? 0)
                    )
            );
            setActiveProducts(actProducts);
        }
    }, [productsDataHeater]);
    useEffect(() => {
        let tabItems = 0;
        if (smallProducts.length > 0) {
            tabItems++;
            tabRefSmall.current?.click();
        }
        if (mediumProducts.length > 0) {
            if(tabItems === 0) {
                tabRefMedium.current?.click();
            }
            tabItems++;
            // tabRefMedium.current?.click();
        }
        if (largeProducts.length > 0) {
            if(tabItems === 0) {
                tabRefMedium.current?.click();
            }
            tabItems++;
            // tabRefLarge.current?.click();
        }
        setTabs(tabItems);
    }, [smallProducts, mediumProducts, largeProducts]);

    useScrollFadeIn("fade-in", [activeProducts]);
    useScrollFadeIn("fade-in-up", [activeProducts]);

    // const CpCard = (props: {style?: React.CSSProperties}) => {
    //     return (
    //         <div className="cp_card" style={props.style}>
    //             <p className="cp_card__title noto-sans-jp-extrabold">期間限定！<span className="inter-semibold cp_card__date">2026/3/5</span><span className="cp_card__datejp">（木）</span>まで</p>
    //             <p className="cp_card__text1 noto-sans-jp-bold">今だけガスファンヒーターをご購入された方に</p>
    //             <p className="cp_card__text2 noto-sans-jp-extrabold">Amazonギフトカード等に交換できる
    //                 <img className="cp_card__amazon_card" src="/assets/img/pr_fh/amazon-card.png" alt="AmazonGiftCard" />
    //             </p>
    //             <p className="cp_card__price_text noto-sans-jp-bold">
    //                 <span className="cp_card__price inter-bold">2,000</span><span className="cp_card__price_text__unit noto-sans-jp-black">マイル</span>をもれなくプレゼント！<span className="cp_card__price_text__rice">※</span>
    //             </p>
    //             <p className="cp_card__price_small noto-sans-jp-bold">
    //             （<span className="inter-bold">2,000</span>円相当）
    //             </p>
    //             <div className="cp_card__annotation_wrap" >
    //                 <p className="cp_card__annotation noto-sans-jp-regular">※西部ガスの無料会員サービス「<span onClick={toggleDisplay} className="blueLink" style={{cursor: "pointer"}}>SAIBU LAND</span>」でご利用いただけます</p>
    //                 <p className="cp_card__annotation noto-sans-jp-regular">※キャンペーンの詳細は<a href="#cp-terms" className="blueLink">こちら</a></p>
    //             </div>
    //         </div>
    //     )
    // }
    return (
        <>
            <Helmet>
                <title>
                    【2万円台から買えるガスファンヒーター】配送・処分・ガスコードすべて無料！
                </title>
                <link
                    rel="canonical"
                    href="https://onlineshop.saibugas.co.jp/pr_fh/"
                />
                <meta
                    name="description"
                    content="「肌寒い時に、5秒で温まる」2万円台から買える西部ガスのガスファンヒーター。配送・処分・ガスコードすべて無料で、さらに5年間の長期品質保証付き！アウトレット商品は在庫限り！"
                />
                <meta
                    property="og:title"
                    content="【2万円台から買えるガスファンヒーター】配送・処分・ガスコードすべて無料！"
                />
                <meta
                    property="og:description"
                    content="「肌寒い時に、5秒で温まる」2万円台から買える西部ガスのガスファンヒーター。配送・処分・ガスコードすべて無料で、さらに5年間の長期品質保証付き！アウトレット商品は在庫限り！"
                />
                <meta
                    property="og:url"
                    content="https://onlineshop.saibugas.co.jp/pr_fh/"
                />
                <meta
                    property="og:site_name"
                    content="【2万円台から買えるガスファンヒーター】配送・処分・ガスコードすべて無料！"
                />
                <meta property="og:image" content="/assets/img/pr_fh/ogp.jpg" />
                <meta name="robots" content="index,follow" />

                <style>{`html { font-size: 62.5% !important; }`}</style>
            </Helmet>
            <div className="wrap page-pr_fh" id="pageTop">
				<Modal isOpen={display} onClose={toggleDisplay} style={boxStyle} >                    
                    <h2 className="ttl noto-sans-jp-extrabold" style={{textAlign: "center",fontSize: "2.4rem",marginBottom: "3rem"}}>SAIBU LANDとは？</h2>
                    <img src="/assets/img/common/saibuland_img.png" alt="SAIBU LAND" style={{width: "90%",margin: "0 auto"}} />
                    <p className="noto-sans-jp-medium" style={{lineHeight: "1.66",margin:"2rem 0",fontSize: "1.5rem"}}>SAIBU LANDは、毎日のくらしが「ちょっと豊かに、楽しくなる」情報やサービスをお届けする、<span className="yellow-underline">お得なマイルを貯めたり・使ったりすることができる無料の会員サービス</span>です。<br/><br/><span className="yellow-underline">LINEでカンタンに入園(入会)</span>することができます。</p>
                    <Button variant="pink" ><a href="https://www.saibugas.co.jp/home/saibuland/" target="_blank" className="blank">詳しくはこちら</a></Button>
                </Modal>
                <ScrollToTopButton />
                <div className="sideNav fade-in-up">
                    <nav>
                        <div className="navBox">
                            <h2>MENU</h2>
                            <ul className="smoothLink">
                                <li>
                                    <a
                                        href="#point5"
                                        id="fhpr2024_navipc_point"
                                    >
                                        ガスファンヒーターが
                                        <br />
                                        おすすめな5つのPoint
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#if_buy"
                                        id="fhpr2024_navipc_merit"
                                    >
                                        西部ガスで購入するメリット
                                    </a>
                                </li>
                                <li>
                                    <a href="#push" id="fhpr2024_navipc_select">
                                        おすすめファンヒーター
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#heater"
                                        id="fhpr2024_navipc_product"
                                    >
                                        ガスファンヒーター一覧
                                    </a>
                                </li>
                                <li>
                                    <a href="#flow" id="fhpr2024_navipc_flow">
                                        ご利用の流れ
                                    </a>
                                </li>
                                <li>
                                    <a href="#area" id="fhpr2024_navipc_area">
                                        配送対応エリア
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#payment"
                                        id="fhpr2024_navipc_payment"
                                    >
                                        お支払い方法
                                    </a>
                                </li>
                                <li>
                                    <a href="#faq" id="fhpr2024_navipc_faq">
                                        よくあるご質問
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#contact"
                                        id="fhpr2024_navipc_contact"
                                    >
                                        お問い合わせ
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </div>
                <div className="sideQr fade-in-up">
                    <p>
                        <img
                            src="/assets/img/pr_fh/qr.png"
                            alt="スマホからのアクセスはこちら"
                            loading="eager"
                        />
                    </p>
                </div>
                <main className={isActive ? "open" : ""}>
                    <div
                        className="spMenuSet"
                        onClick={() => setIsActive(!isActive)}
                    >
                        {" "}
                        <a className="" id="nav-toggle">
                            <div>
                                {" "}
                                <span></span> <span></span> <span></span>{" "}
                            </div>
                        </a>{" "}
                    </div>
                    <header>
                        <h1 className="logo-wrap">
                            <a href="/">
                                <span className="logo">
                                    <img
                                        src="/assets/img/pr_tc/logo.png"
                                        alt="西部ガスグループ"
                                    />
                                </span>
                                <span className="logo-sub">公式オンラインショップ</span>
                            </a>
                        </h1>
                    </header>
                    <nav id="global-nav">
                        <div className="gnInr">
                            <ul className="gnBox smoothLinkSP">
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#point5" id="fhpr2024_menu_point">
                                        ガスファンヒーターが
                                        <br />
                                        おすすめな5つのPoint
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#if_buy" id="fhpr2024_menu_merit">
                                        西部ガスで購入するメリット
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#push" id="fhpr2024_menu_select">
                                        おすすめファンヒーター
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#heater"
                                        id="fhpr2024_menu_product"
                                    >
                                        ガスファンヒーター一覧
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#flow" id="fhpr2024_menu_flow">
                                        ご利用の流れ
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#area" id="fhpr2024_menu_area">
                                        配送対応エリア
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#payment"
                                        id="fhpr2024_menu_payment"
                                    >
                                        お支払い方法
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a href="#faq" id="fhpr2024_menu_faq">
                                        よくあるご質問
                                    </a>
                                </li>
                                <li onClick={() => setIsActive(false)}>
                                    <a
                                        href="#contact"
                                        id="fhpr2024_menu_contact"
                                    >
                                        お問い合わせ
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </nav>
                    <div className="contents">
                        <section className="mv">
                            <h1>
                                <picture>
                                    {/* WebP形式のソース */}
                                    <source
                                        srcSet="/assets/img/pr_fh/mv.webp"
                                        type="image/webp"
                                    />
                                    <source
                                        srcSet="/assets/img/pr_fh/mv.jpg"
                                        type="image/jpeg"
                                    />
                                    <img
                                        src="/assets/img/pr_fh/mv.jpg"
                                        alt="「肌寒い時に、5秒で温まる」2万円台から買える西部ガスファンヒーター ※都市ガス用の商品です"
                                        loading="eager"
                                        style={{ margin: "0 auto" }}
                                    />
                                </picture>
                            </h1>
                            <div className="mv_bottom">
                                <div className="mv_bottom-inr">
                                    <h2 className="ttl center">
                                        <span>
                                            西部ガスでガスファンヒーターを購入すると
                                        </span>
                                    </h2>
                                    <ul className="mv_bottom-list">
                                        <li>
                                            <span className="grd">
                                                ５年間の長期品質保証
                                            </span>
                                            付き！
                                        </li>
                                        <li>
                                            配送・処分・ガスコード
                                            <span className="grd">
                                                すべて無料
                                            </span>
                                            ！
                                        </li>
                                        <li>
                                            使用方法を
                                            <span className="grd">
                                                しっかりご説明
                                            </span>
                                            ！
                                        </li>
                                    </ul>
                                </div>
                            {/* <CpCard />     */}
                            </div>
                        </section>
                        {/* <section className="news fade-in">
                            <div className="cont_inr">
                                <ul className="news_list">
                                    <li className="news_item">
                                        <img
                                            className="news_icon"
                                            src="/assets/img/pr_fh/att_w.png"
                                            alt="重要"
                                        />
                                        <a href="#" className="news_link">
                                            <span className="news_date">
                                                2025/07/31
                                            </span>
                                            <p className="news_title">
                                                重大なお知らせが入ります。重大なお知らせが入ります。重大なお知らせが入ります。
                                            </p>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </section> */}
                        
                        <section className="attentionSec">
                            <a
                                href="#heater"
                                id="fhpr2024_mv_cv"
                                className="green_btn arrow fade-in-up noto-sans-jp-black"
                            >
                                {summaryDataHeater?.max_discount_rate &&
                                summaryDataHeater?.max_discount_rate > 0 ? (
                                    <span className="fukidashi">
                                        通常価格より最大
                                        <span className="num">
                                            {
                                                summaryDataHeater?.max_discount_rate
                                            }
                                        </span>
                                        %OFF
                                    </span>
                                ) : (
                                    <>
                                        {summaryDataHeater?.has_outlet_products && (
                                            <img
                                                className="outlet_icon"
                                                src="/assets/img/common/outlet.png"
                                                alt="アウトレット"
                                            />
                                        )}
                                    </>
                                )}
                                ガスファンヒーター一覧
                            </a>
                            <div className="attention fade-in-up">
                                <h3 className="inr_ttl bold">
                                    ガスファンヒーターを使用するには
                                    <br />
                                    ガス栓が必要になります。
                                </h3>
                                <p className="txt bold">
                                    ご自宅のガス栓の有無を確認してください。
                                    ガス栓が無い場合は、工事によりガス栓を増設することができます。
                                </p>
                                <ul className="small_txt indent">
                                    <li>
                                        ガス栓増設工事代金は、無料でお見積りいたします。
                                    </li>
                                    <li>
                                        ご自宅にガス栓が無く、工事が必要な場合は、ご注文情報入力時に「注文備考」欄にその旨をご記入ください。
                                    </li>
                                </ul>
                            </div>
                        </section>
                        <section id="point5" className="point5">
                            <div className="cont_inr">
                                <div className="flex cont_ttl-wrap">
                                    <h2 className="cont_ttl fade-in-up">
                                        <small>
                                            ガスファンヒーターが
                                            <br />
                                            おすすめな
                                        </small>
                                        <span className="en">5</span>つの
                                        <span className="en">Point</span>
                                    </h2>
                                    <figure className="ppl fade-in-up">
                                        <img
                                            src="/assets/img/pr_fh/point5_img.png"
                                            alt=""
                                        />
                                    </figure>
                                </div>
                                <div className="point_wrap">
                                    <motion.div
                                        className="flow1 fade-in-up"
                                        initial={false}
                                        animate={{
                                            height: accActive[0]
                                                ? "auto"
                                                : "103rem",
                                        }}
                                        transition={{
                                            duration: 1,
                                            ease: [0.25, 0.46, 0.45, 0.94],
                                        }}
                                        style={{
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div className="content-wrap">
                                            <div className="point_cont p01 fade-in-up">
                                                <h3 className="point_ttl">
                                                    スイッチオンから
                                                    <br />
                                                    わずか５秒でスピード温風
                                                </h3>
                                                <p className="txt">
                                                    「おはよう」と同時にスイッチを入れて、
                                                    <span className="org_txt">
                                                        わずか５秒でパワフルな温風
                                                    </span>
                                                    が吹き出します。寒い朝でも、あっという間にお部屋が暖まるので、寝起きも快適！忙しい朝もテキパキ行動できて、時間に余裕が生まれます。
                                                </p>
                                                <figure>
                                                    <img
                                                        src="/assets/img/pr_fh/point01_img.jpg"
                                                        alt="イメージ図"
                                                    />
                                                </figure>
                                            </div>
                                            <div className="point_cont p02 fade-in-up">
                                                <h3 className="point_ttl">
                                                    ニオイも気にならず、
                                                    <br />
                                                    暖めすぎを抑えるから
                                                    <br />
                                                    お部屋は快適
                                                </h3>
                                                <p className="txt">
                                                    <span className="org_txt">
                                                        エコ運転機能
                                                    </span>
                                                    なら、お部屋の暖めすぎを自動で抑えて
                                                    <span className="org_txt">
                                                        適温をキープ
                                                    </span>
                                                    するので、部屋全体を快適に保ちます。また、冬の閉め切ったお部屋でも、点火・消火時の嫌なニオイがほとんどありません。
                                                    <br />
                                                    <span className="org_txt">
                                                        省エネでお財布にも環境にもやさしく
                                                    </span>
                                                    、快適に過ごせます。
                                                </p>
                                                <figure>
                                                    <img
                                                        src="/assets/img/pr_fh/point02_img.jpg"
                                                        alt="室温が比較的温かい場合のグラフ"
                                                    />
                                                </figure>
                                            </div>
                                            <div className="point_cont p03 fade-in-up">
                                                <h3 className="point_ttl">
                                                    ガスコンセントにつなぐだけ！軽くてコンパクトだから移動もラクラク
                                                </h3>
                                                <p className="txt">
                                                    ガス栓にガスコードをつなぐだけで使用できます。
                                                    <span className="org_txt">
                                                        燃料切れの心配や、燃料補給の手間もかからず
                                                    </span>
                                                    、寒い冬も安心です。もちろん、燃料の買い置きや、保存場所の確保も不要です。
                                                </p>
                                                <figure className="inr_img">
                                                    <img
                                                        src="/assets/img/pr_fh/point03_img.jpg"
                                                        alt="イメージ図"
                                                    />
                                                </figure>
                                                <p className="txt">
                                                    本体に燃料タンクがないので、デザイン性が高く、お部屋のインテリアとしてもマッチします。しかも、
                                                    <span className="org_txt">
                                                        軽くてコンパクトだから移動もラクラク
                                                    </span>
                                                    。お掃除も収納も、とにかくカンタンなのが魅力です！
                                                </p>
                                            </div>
                                            <div className="point_cont p04 fade-in-up">
                                                <h3 className="point_ttl">
                                                    もしもの時に備えて
                                                    <br />
                                                    ８つの安心・安全機能を
                                                    <br />
                                                    全機種に搭載
                                                </h3>
                                                <p className="txt">
                                                    西部ガスグループで取り扱いのあるガスファンヒーターは、転倒時や停電時にガスを止めて運転を停止する機能や、不完全燃焼を起こす前にガスを自動的に停止する装置など、
                                                    <span className="org_txt">
                                                        ８つの安心・安全機能を全機種に搭載
                                                    </span>
                                                    。小さなお子さまやご年配の方がいるご家庭でも、安心してお使いいただけます。
                                                </p>
                                                <figure className="inr_img">
                                                    <img
                                                        src="/assets/img/pr_fh/point04_img.jpg"
                                                        alt="イメージ図"
                                                    />
                                                </figure>
                                                <div className="gr_box">
                                                    <p className="gr_box_ttl">
                                                        ８つの安心・安全機能
                                                    </p>
                                                    <ul className="list w_50">
                                                        <li>
                                                            転倒時ガス遮断装置
                                                        </li>
                                                        <li>過熱防止装置</li>
                                                        <li>立消え安全装置</li>
                                                        <li>停電時安全装置</li>
                                                        <li>
                                                            不完全燃焼防止装置
                                                        </li>
                                                        <li>
                                                            ８時間自動消火機能
                                                        </li>
                                                        <li>過電流防止装置</li>
                                                        <li>ロック機能</li>
                                                    </ul>
                                                </div>
                                            </div>
                                            <div className="point_cont p05 fade-in-up">
                                                <h3 className="point_ttl">
                                                    ランニングコストをセーブ！
                                                </h3>
                                                <p className="txt">
                                                    ガスファンヒーターは、
                                                    <span className="org_txt">
                                                        ランニングコストを抑えて
                                                    </span>
                                                    、しっかりと部屋全体を暖めます。寒い冬に欠かせない暖房なので、ちゃんと節約します。
                                                </p>
                                                <div className="table_wrap">
                                                    <p className="table_wrap-ttl">
                                                        一般料金プランの場合
                                                    </p>
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <th>
                                                                    運転開始から
                                                                    <br />
                                                                    ３時間
                                                                </th>
                                                                <th>
                                                                    運転開始から
                                                                    <br />
                                                                    ８時間
                                                                </th>
                                                                <th>
                                                                    １時間あたり
                                                                    <br />
                                                                    （８時間運転時）
                                                                </th>
                                                            </tr>
                                                            <tr>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    118{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    228{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    28{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="table_wrap">
                                                    <p className="table_wrap-ttl">
                                                        家庭用厨房・給湯・暖房契約（ホームメリット)プランの場合
                                                    </p>
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <th>
                                                                    運転開始から
                                                                    <br />
                                                                    ３時間
                                                                </th>
                                                                <th>
                                                                    運転開始から
                                                                    <br />
                                                                    ８時間
                                                                </th>
                                                                <th>
                                                                    １時間あたり
                                                                    <br />
                                                                    （８時間運転時）
                                                                </th>
                                                            </tr>
                                                            <tr>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    77{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    149{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <small>
                                                                        約
                                                                    </small>{" "}
                                                                    19{" "}
                                                                    <small>
                                                                        円
                                                                    </small>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <ul className="small_txt indent">
                                                    <li>
                                                        ガス代と電気代を合わせた料金です。
                                                    </li>
                                                    <li>
                                                        2024年9月時点の料金単価(一般料金C(30㎥を超え100㎥まで)・ホームメリット冬期D(61㎥を超える場合)・プラスでんきプラン1)にて算出しています。(45MJ地区)
                                                    </li>
                                                    <li>
                                                        2024年9月時点の消費税(10%)・原料費調整単価・燃料費等調整単価を含みます。
                                                    </li>
                                                    <li>
                                                        機種、ご使用方法、お部屋の大きさ、断熱条件等により実際の料金と異なる場合があります。
                                                    </li>
                                                    <li>
                                                        {" "}
                                                        基本料金は含みません。
                                                    </li>
                                                </ul>
                                                <div className="gr_box">
                                                    <p className="gr_box_ttl">
                                                        ガスファンヒーターランニングコストについて
                                                    </p>
                                                    <ul className="list small">
                                                        <li>
                                                            測定機種：暖房能力4.0kWタイプ
                                                        </li>
                                                        <li>
                                                            環境条件：外気温5℃・湿度65％、室内初期温度9℃
                                                        </li>
                                                        <li>
                                                            室内温度設定：22℃
                                                        </li>
                                                        <li>
                                                            断熱仕様：新省エネ基準Ⅳ地区相当
                                                        </li>
                                                        <li>室内広さ：10畳</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                    <div
                                        className="more-btn"
                                        onClick={() =>
                                            setAccActive([
                                                !accActive[0],
                                                accActive[1],
                                                accActive[2],
                                            ])
                                        }
                                    >
                                        {accActive[0] ? (
                                            <span className="close">
                                                閉じる
                                            </span>
                                        ) : (
                                            <span className="open">
                                                もっと見る
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <a
                                href="#heater"
                                id="fhpr2024_mv_cv"
                                className="green_btn arrow fade-in-up noto-sans-jp-black"
                            >
                                {summaryDataHeater?.max_discount_rate &&
                                summaryDataHeater?.max_discount_rate > 0 ? (
                                    <span className="fukidashi">
                                        通常価格より最大
                                        <span className="num">
                                            {
                                                summaryDataHeater?.max_discount_rate
                                            }
                                        </span>
                                        %OFF
                                    </span>
                                ) : (
                                    <>
                                        {summaryDataHeater?.has_outlet_products && (
                                            <img
                                                className="outlet_icon"
                                                src="/assets/img/common/outlet.png"
                                                alt="アウトレット"
                                            />
                                        )}
                                    </>
                                )}
                                ガスファンヒーター一覧
                            </a>
                        </section>
                        <section id="if_buy" className="if_buy">
                            <div className="cont_inr">
                                <h2 className="cont_ttl fade-in-up">
                                    <small>ガスファンヒーターを</small>
                                    西部ガスで購入すると
                                </h2>
                                <div className="if_buy-box box01 fade-in-up">
                                    <h3 className="check_or_ttl">
                                        ５年間の長期品質保証付き！
                                    </h3>
                                    <p className="txt">
                                        品質に自信があるから、西部ガスのガスファンヒーターは５年間の保証付き。通常使用の場合の故障は無償で修理します。安心してお使いください。
                                    </p>
                                    <ul className="small_txt indent">
                                        <li>
                                            ガスファンヒーターの一部機種、ストーブ、FF暖房機は除きます。
                                        </li>
                                        <li>
                                            修理をご依頼いただいた時点で西部ガスの都市ガスをご利用されている場合に限ります。
                                        </li>
                                    </ul>
                                </div>
                                <div className="if_buy-box box02 fade-in-up">
                                    <h3 className="check_or_ttl">
                                        配送・処分・ガスコード
                                        <br />
                                        すべて無料！
                                    </h3>
                                    <p className="txt">
                                        {" "}
                                        西部ガスなら送料・配達料が無料！また、不要なガスファンヒーターがある場合、無料で処分をお引き受けします。
                                    </p>
                                    <p className="txt">
                                        さらに、通常別売りの専用ガスコード(1m〜3mのいずれか)を無料でプレゼント！商品本体の費用以外はいただきません。
                                    </p>
                                    <ul className="small_txt indent">
                                        <li>
                                            ご自宅にガス栓が無く、工事が必要な場合は、ガス栓増設工事費用が必要になります。
                                        </li>
                                    </ul>
                                </div>
                                <div className="if_buy-box box03 fade-in-up">
                                    <h3 className="check_or_ttl">
                                        使用方法をしっかりご説明！
                                    </h3>
                                    <p className="txt">
                                        ガス機器のプロである西部ガスリビングメイトが、商品のお届け時に、使用方法だけでなく点検、修理など先々のフォローまでしっかりご説明いたします。
                                    </p>
                                </div>
                            </div>
                        </section>
                        <section id="push" className="pushSec">
                            <h2 className="fade-in-up">
                                <img
                                    src="/assets/img/pr_fh/push_h.png"
                                    alt="おすすめファンヒーター"
                                    loading="lazy"
                                />
                            </h2>
                            <ol className="pushList">
                                {recommendedProducts?.length > 0 &&
                                    recommendedProducts
                                        ?.slice(0, 3)
                                        .map((product, index) => (
                                            <li
                                                className={`li0${
                                                    index + 1
                                                } fade-in-up`}
                                                key={index}
                                            >
                                                <h3>
                                                    {" "}
                                                    <span className="num">
                                                        {index + 1}
                                                    </span>{" "}
                                                    <span className="txt">
                                                        {
                                                            product.recommended_headline
                                                        }
                                                    </span>{" "}
                                                </h3>
                                                <p className="img">
                                                    <img
                                                        src={normalizeImagePath(product.eyecatch_image_url)}
                                                        alt={product.name ?? ""}
                                                        loading="lazy"
                                                    />
                                                </p>
                                                <p className="desc">
                                                    {product.recommended_body}
                                                </p>
                                                <button
                                                    className="green_btn arrow noto-sans-jp-black"
                                                    onClick={() =>
                                                        handleRecommendedClick(
                                                            product.id
                                                        )
                                                    }
                                                >
                                                    詳細を見る
                                                </button>
                                            </li>
                                        ))}
                            </ol>
                        </section>

                        {/* ガスファンヒーター一覧 */}
                        <section id="heater" className="heater">
                            <h2 className="cont_ttl">
                                <small>
                                    <span className="en">OUTLET</span>
                                    商品は在庫限り！
                                </small>
                                ガスファンヒーター一覧
                            </h2>
                            <ul className="small_txt indent">
                                <li>
                                    アウトレット商品は、外装梱包にキズや汚れがある場合がございます。キズや汚れの状態は商品により異なります。予めご了承ください。
                                </li>
                            </ul>
                            {/* <div style={{padding:"0 1.5rem"}}>
                                <CpCard style={{marginBottom:"40px"}}/>
                            </div> */}
                            <div id="tab_area" className="tab_area">
                                {/* 木造〜11畳 */}
                                {smallProducts.length > 0 && (
                                    <>
                                        <input
                                            type="radio"
                                            name="tab"
                                            id="tab1"
                                            ref={tabRefSmall}
                                        />
                                        <label
                                            className={`btn_tab ${
                                                tabItems > 0
                                                    ? `width_${tabItems}`
                                                    : ""
                                            }`}
                                            htmlFor="tab1"
                                        >
                                            木造〜11畳
                                            <small>(鉄筋〜15畳)</small>
                                        </label>
                                        <div
                                            id="motion_area"
                                            className="tabcont_inr"
                                        >
                                            <ul className="product_list inner">
                                                {generateProducts(
                                                    smallProducts
                                                )}
                                            </ul>
                                        </div>
                                    </>
                                )}

                                {/* 木造〜12畳 */}
                                {mediumProducts.length > 0 && (
                                    <>
                                        <input
                                            type="radio"
                                            name="tab"
                                            id="tab2"
                                            ref={tabRefMedium}
                                        />
                                        <label
                                            className={`btn_tab ${
                                                tabItems > 0
                                                    ? `width_${tabItems}`
                                                    : ""
                                            }`}
                                            htmlFor="tab2"
                                        >
                                            木造〜12畳
                                            <small>(鉄筋〜16畳)</small>
                                        </label>
                                        <div
                                            id="motion_area2"
                                            className="tabcont_inr"
                                        >
                                            <ul className="product_list inner">
                                                {generateProducts(
                                                    mediumProducts
                                                )}
                                            </ul>
                                        </div>
                                    </>
                                )}

                                {/* 木造〜15畳 */}
                                {largeProducts.length > 0 && (
                                    <>
                                        <input
                                            type="radio"
                                            name="tab"
                                            id="tab3"
                                            ref={tabRefLarge}
                                        />
                                        <label
                                            className={`btn_tab ${
                                                tabItems > 0
                                                    ? `width_${tabItems}`
                                                    : ""
                                            }`}
                                            htmlFor="tab3"
                                        >
                                            木造〜15畳
                                            <small>(鉄筋〜21畳)</small>
                                        </label>
                                        <div
                                            id="motion_area3"
                                            className="tabcont_inr"
                                        >
                                            <ul className="product_list inner">
                                                {generateProducts(
                                                    largeProducts
                                                )}
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>
                        <section className="flowSec" id="flow">
                            <h2 className="fade-in-up">
                                ご利用の流れ<span>FLOW</span>
                            </h2>
                            <dl
                                className="accDl flow1"
                                onClick={() =>
                                    setAccActive([
                                        accActive[0],
                                        !accActive[1],
                                        accActive[2],
                                    ])
                                }
                            >
                                <dt className={`${accActive[1] ? "open" : ""}`}>
                                    <h3>ご注文手順</h3>
                                    <p className="arrow"></p>
                                </dt>
                                <motion.div
                                    className="flow1 fade-in-up"
                                    initial={false}
                                    animate={{
                                        height: accActive[1] ? "auto" : 0,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        overflow: "hidden",
                                    }}
                                >
                                    <motion.dd
                                        initial={false}
                                        animate={{
                                            opacity: accActive[1] ? 1 : 0,
                                            y: accActive[1] ? 0 : -10,
                                        }}
                                        transition={{
                                            duration: 0.35,
                                            delay: accActive[1] ? 0.1 : 0,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <ol className="exList">
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        1
                                                    </span>
                                                    ご購入商品の選択
                                                </h4>
                                                <p>
                                                    購入したい商品の
                                                    「注文画面へ」ボタンをクリックします。ご注文情報入力画面へ遷移します。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        2
                                                    </span>
                                                    ご注文情報の入力・確認
                                                </h4>
                                                <p>
                                                    お名前やお届け先等、必要な情報を入力ください。最後に入力内容と商品名、数量、金額をご確認いただき間違いなければ「注文を確定する」ボタンをクリックします。
                                                    <br />
                                                    ※ご購入には携帯番号のご入力が必須です。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        3
                                                    </span>
                                                    お支払情報の入力・確認
                                                </h4>
                                                <p>
                                                    お支払情報を入力ください。
                                                    <br />
                                                    最後に入力内容を確認いただき問題なければ支払完了ボタンをクリックします。
                                                </p>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        4
                                                    </span>
                                                    ご注文完了
                                                </h4>
                                                <p>
                                                    「ご注文いただきありがとうございました」という画面が表示されましたらご購入完了です。ご入力いただいた携帯電話番号に購入完了通知（SMS）をお送りしますので、ご確認ください。
                                                </p>
                                            </li>
                                        </ol>
                                    </motion.dd>
                                </motion.div>
                            </dl>
                            <dl
                                className="accDl flow2"
                                onClick={() =>
                                    setAccActive([
                                        accActive[0],
                                        accActive[1],
                                        !accActive[2],
                                    ])
                                }
                            >
                                <dt className={`${accActive[2] ? "open" : ""}`}>
                                    <h3 className="shouhin_ttl">
                                        商品のお届け
                                    </h3>
                                    <p className="arrow"></p>
                                </dt>
                                <motion.div
                                    className="flow1 fade-in-up"
                                    initial={false}
                                    animate={{
                                        height: accActive[2] ? "auto" : 0,
                                    }}
                                    transition={{
                                        duration: 0.35,
                                        ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                    style={{
                                        overflow: "hidden",
                                    }}
                                >
                                    <motion.dd
                                        initial={false}
                                        animate={{
                                            opacity: accActive[2] ? 1 : 0,
                                            y: accActive[2] ? 0 : -10,
                                        }}
                                        transition={{
                                            duration: 0.35,
                                            delay: accActive[2] ? 0.1 : 0,
                                            ease: "easeOut",
                                        }}
                                    >
                                        <ol className="exList arrowList">
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        1
                                                    </span>
                                                    もよりの西部ガス販売店
                                                    <br />
                                                    (リビングメイト)よりご連絡
                                                </h4>
                                                <p>
                                                    もよりの西部ガス販売店
                                                    (リビングメイト)
                                                    より、お支払い手続き完了後、お客さまへ日祝日を除く3日以内にお電話でご連絡いたします。
                                                </p>
                                                <ul className="small_txt indent">
                                                    <li>
                                                        ご連絡の際は、ご注文内容、お届け先、お届け日、お客さま情報の確認をさせていただきます。
                                                    </li>
                                                </ul>
                                            </li>
                                            <li>
                                                <h4>
                                                    <span className="num">
                                                        2
                                                    </span>
                                                    お届け・設置
                                                </h4>
                                                <p>
                                                    確認させていただいたご指定日にお届けいたします。
                                                </p>
                                            </li>
                                        </ol>
                                    </motion.dd>
                                </motion.div>
                            </dl>
                            <div className="attention fade-in-up">
                                <h3 className="inr_ttl bold">
                                    ガスファンヒーターを使用するには
                                    <br />
                                    ガス栓が必要になります。
                                </h3>
                                <p className="txt bold">
                                    ご自宅のガス栓の有無を確認してください。
                                    ガス栓が無い場合は、工事によりガス栓を増設することができます。
                                </p>
                                <ul className="small_txt indent">
                                    <li>
                                        ガス栓増設工事代金は、無料でお見積りいたします。
                                    </li>
                                    <li>
                                        ご自宅にガス栓が無く、工事が必要な場合は、ご注文情報入力時に「注文備考」欄にその旨をご記入ください。
                                    </li>
                                </ul>
                            </div>
                        </section>
                        <section className="areaSec" id="area">
                            <h2 className="fade-in-up">
                                配送対応エリア<span>AREA</span>
                            </h2>
                            <p className="fade-in-up">
                                西部ガス・西部ガス熊本・西部ガス佐世保・西部ガス長崎のサービスエリア内*が配送エリアとなります。
                            </p>
                            <ul className="small_txt indent fade-in-up">
                                <li>
                                    福岡・熊本・長崎の3県15市15町。詳しくは
                                    <a
                                        href="https://hd.saibugas.co.jp/business/gas/#serviceArea"
                                        target="_blank"
                                    >
                                        こちら
                                    </a>
                                    をご確認ください。
                                </li>
                            </ul>
                        </section>
                        <section className="paymentSec" id="payment">
                            <h2 className="fade-in-up">
                                お支払い方法<span>PAYMENT</span>
                            </h2>
                            <dl className="accDl payment1 action fade-in-up">
                                <dd>
                                    <div className="paymentWeb">
                                        <p>
                                            クレジットカード払いまたはPayPayがご利用いただけます。
                                        </p>
                                        <div className="whiteBox">
                                            <h3>
                                                ご利用可能なクレジットブランド
                                            </h3>
                                            <ul>
                                                <li>・MasterCard</li>
                                                <li>・Diners Club</li>
                                                <li>・VISA</li>
                                                <li>・JCB</li>
                                                <li>・American Express</li>
                                                <li>・DISCOVER</li>
                                            </ul>
                                        </div>
                                    </div>
                                </dd>
                            </dl>
                            <p className="attMini fade-in-up">
                                ※お支払い期限については
                                <a href={userGuide} target="_blank">
                                    こちら
                                </a>
                                をご覧ください
                            </p>
                        </section>
                        <section className="faqSec" id="faq">
                            <h2 className="fade-in-up">
                                よくあるご質問<span>FAQ</span>
                            </h2>
                            <ul className="faqList fade-in-up">
                                {faqList.map((faq, index) => (
                                    <li key={index}>
                                        <dl
                                            onClick={() => {
                                                const newState = [...faqActive];
                                                newState[index] =
                                                    !newState[index];
                                                setFaqActive(newState);
                                            }}
                                        >
                                            <dt
                                                className={`${
                                                    faqActive[index]
                                                        ? "open"
                                                        : ""
                                                }`}
                                            >
                                                {faq.q}
                                            </dt>
                                            <motion.div
                                                className="faq-answer-wrapper"
                                                initial={false}
                                                animate={{
                                                    height: faqActive[index]
                                                        ? "auto"
                                                        : 0,
                                                }}
                                                transition={{
                                                    duration: 0.35,
                                                    ease: [
                                                        0.25, 0.46, 0.45, 0.94,
                                                    ],
                                                }}
                                                style={{
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <motion.dd
                                                    className="flow1"
                                                    initial={false}
                                                    animate={{
                                                        opacity: faqActive[
                                                            index
                                                        ]
                                                            ? 1
                                                            : 0,
                                                        y: faqActive[index]
                                                            ? 0
                                                            : -10,
                                                    }}
                                                    transition={{
                                                        duration: 0.35,
                                                        delay: faqActive[index]
                                                            ? 0.1
                                                            : 0,
                                                        ease: "easeOut",
                                                    }}
                                                >
                                                    {faq.a}
                                                </motion.dd>
                                            </motion.div>
                                        </dl>
                                    </li>
                                ))}
                            </ul>
                        </section>
                        {/* <section id="cp-terms" className="termsSec">
                            <h2 className="fade-in-up">
                                キャンペーン規約
                            </h2>
                            <div className="cp-terms-content">
                                <img className="pdf-icon" src="/assets/img/common/pdf-icon.svg" alt="キャンペーン規約" style={{width: "2rem",height: "2rem"}}/>
                                <a href={campaignDetails} target="_blank">
                                    キャンペーン規約
                                </a>
                            </div>
                        </section> */}
                        <section className="contactSec" id="contact">
                            <div className="cont_inr fade-in-up">
                                <img
                                    src="/assets/img/common/mail.svg"
                                    alt="メールでのお問い合わせ"
                                    className="mail_icon"
                                />
                                <h2>メールでのお問い合わせ</h2>
                                <p className="bHour">
                                    （受付時間：24時間365日受付）
                                </p>
                                <MailButton className="red_btn fade-in-up">
                                    お問い合わせ
                                </MailButton>
                                <p className="mail_annotation fade-in-up">
                                    ※件名は必ず変更せずにご送信ください。
                                    <br />
                                    ※お問い合わせ後5営業日以内を目処にご回答いたします。
                                    <br />
                                    ※メーラーが自動で起動しない場合は、お手数ですが「ecshop-info@saibugas-group.com」宛にお問い合わせをお願いいたします 。
                                </p>
                            </div>
                        </section>
                    </div>
                    <Footer />
                </main>
            </div>
        </>
    );
}
